import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { environment } from '../../environments/environment';
import { app_permissions } from '../security/app-permissions';
import { PermissionService } from '../security/permission.service';
import { PageDataPanelComponent } from '../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../shared/page-layout/page-shell.component';
import { PageMetric } from '../shared/page-layout/page-layout.models';
import { ApiReferenceComponent } from './api-reference.component';
import {
  ApiClientCreateRequest,
  ApiClientCreateResponse,
  ApiClientSecretRotationResponse,
  ApiClientView,
  ApiPlatformSummary,
  ApiRateLimitPolicy,
  ApiScopeDefinition,
  ApiUsageLogView,
  WebhookSecretRotationResponse,
  WebhookSubscriptionView
} from './developer-platform.model';
import { DeveloperPlatformService } from './developer-platform.service';

type PlatformTab = 'dashboard' | 'credentials' | 'usage' | 'webhooks' | 'reference' | 'playground';
type PlaygroundMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface PlaygroundEndpoint {
  label: string;
  method: PlaygroundMethod;
  path: string;
  scope: string;
  description: string;
}

interface PlaygroundResult {
  status: number;
  headers: Record<string, string>;
  body: unknown;
  elapsedMs: number;
}

@Component({
  selector: 'app-developer-platform',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    ConfirmDialogModule,
    DialogModule,
    InputTextModule,
    MultiSelectModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    ToastModule,
    ToggleSwitchModule,
    TooltipModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent,
    ApiReferenceComponent
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './developer-platform.component.html',
  styles: [`
    .dp-grid {
      display: grid;
      gap: 1rem;
    }

    .dp-grid--two,
    .dp-grid--playground {
      grid-template-columns: minmax(0, 1.15fr) minmax(320px, .85fr);
    }

    .dp-form-grid {
      display: grid;
      grid-template-columns: repeat(4, minmax(150px, 1fr));
      gap: .85rem;
      margin-bottom: .85rem;
    }

    .dp-form-grid--webhook {
      grid-template-columns: 1fr 1fr auto;
    }

    label,
    .dp-textarea-field {
      color: #334155;
      display: grid;
      gap: .35rem;
      min-width: 0;
    }

    label span,
    .dp-textarea-field span {
      color: #64748b;
      font-size: .82rem;
      font-weight: 700;
    }

    input,
    textarea,
    p-select,
    p-multiselect {
      width: 100%;
    }

    code,
    .dp-code-block {
      font-family: "SFMono-Regular", Consolas, "Liberation Mono", monospace;
    }

    code {
      color: #172033;
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      display: inline-block;
      max-width: 100%;
      overflow-wrap: anywhere;
      padding: .15rem .3rem;
    }

    .dp-code-block {
      background: #101828;
      border-radius: 8px;
      color: #e6edf6;
      font-size: .78rem;
      line-height: 1.55;
      margin: 0;
      max-height: 440px;
      overflow: auto;
      padding: .85rem;
      white-space: pre-wrap;
      word-break: break-word;
    }

    .dp-secret-panel,
    .dp-panel-toolbar,
    .dp-response-summary,
    .dp-row-actions,
    .dp-inline-actions,
    .dp-play-actions {
      align-items: center;
      display: flex;
      gap: .65rem;
    }

    .dp-secret-panel {
      justify-content: space-between;
    }

    .dp-secret-panel > div:first-child {
      display: grid;
      gap: .35rem;
      min-width: 0;
    }

    .dp-panel-toolbar {
      justify-content: flex-end;
      margin-bottom: .75rem;
    }

    .dp-row-actions,
    .dp-inline-actions {
      justify-content: flex-end;
    }

    .dp-play-actions {
      margin-top: .85rem;
    }

    .dp-stack {
      display: flex;
      flex-direction: column;
      gap: .25rem;
      min-width: 0;
    }

    .dp-stack small {
      color: #64748b;
      font-size: .78rem;
    }

    .dp-chip-list {
      display: flex;
      flex-wrap: wrap;
      gap: .35rem;
      max-width: 320px;
    }

    .dp-chip-list span {
      background: #eef6ff;
      border: 1px solid #cfe5ff;
      border-radius: 6px;
      color: #17324d;
      font-size: .74rem;
      padding: .2rem .35rem;
    }

    .dp-scope-list {
      display: grid;
      gap: .5rem;
      max-height: 420px;
      overflow: auto;
    }

    .dp-scope-row {
      align-items: center;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      display: flex;
      justify-content: space-between;
      gap: .75rem;
      padding: .65rem .75rem;
    }

    .dp-scope-row div {
      display: grid;
      gap: .2rem;
      min-width: 0;
    }

    .dp-scope-row span {
      color: #64748b;
      font-size: .78rem;
      overflow-wrap: anywhere;
    }

    .dp-url-cell {
      max-width: 360px;
      overflow-wrap: anywhere;
    }

    .dp-switch-line {
      align-items: center;
      display: flex;
      gap: .55rem;
      padding-top: 1.35rem;
    }

    .dp-response-summary {
      flex-wrap: wrap;
      margin-bottom: .75rem;
    }

    .dp-response-summary span,
    .dp-empty-state {
      color: #64748b;
      font-size: .86rem;
    }

    @media (max-width: 1100px) {
      .dp-grid--two,
      .dp-grid--playground,
      .dp-form-grid,
      .dp-form-grid--webhook {
        grid-template-columns: 1fr;
      }

      .dp-secret-panel,
      .dp-panel-toolbar {
        align-items: stretch;
        flex-direction: column;
      }
    }
  `]
})
export class DeveloperPlatformComponent implements OnInit {
  private readonly developerPlatformService = inject(DeveloperPlatformService);
  private readonly permissionService = inject(PermissionService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly apiRootUrl = environment.restApiUrl.replace(/\/api\/?$/, '');
  readonly externalBaseUrl = `${environment.restApiUrl.replace(/\/$/, '')}/external/v1`;
  readonly tokenUrl = `${this.apiRootUrl}/oauth2/token`;
  readonly openApiUrl = `${this.apiRootUrl}/v3/api-docs/logilink-enterprise-api`;

  readonly tabs: { value: PlatformTab; label: string; icon: string }[] = [
    { value: 'dashboard', label: 'Dashboard', icon: 'pi pi-chart-line' },
    { value: 'credentials', label: 'Credentials', icon: 'pi pi-key' },
    { value: 'usage', label: 'Usage', icon: 'pi pi-list' },
    { value: 'webhooks', label: 'Webhooks', icon: 'pi pi-send' },
    { value: 'reference', label: 'API Reference', icon: 'pi pi-book' },
    { value: 'playground', label: 'Playground', icon: 'pi pi-code' }
  ];

  readonly environmentOptions = [
    { label: 'Sandbox', value: 'SANDBOX' },
    { label: 'Production', value: 'PRODUCTION' }
  ];

  readonly methodOptions = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map(value => ({ label: value, value }));

  readonly endpoints: PlaygroundEndpoint[] = [
    { label: 'API Status', method: 'GET', path: '/status', scope: 'shipments.read', description: 'Connectivity and principal details' },
    { label: 'Shipments', method: 'GET', path: '/shipments?page=0&pageSize=50', scope: 'shipments.read', description: 'Paged shipment list' },
    { label: 'Deliveries', method: 'GET', path: '/deliveries?page=0&pageSize=50', scope: 'deliveries.read', description: 'Paged delivery list' },
    { label: 'Sales Orders', method: 'GET', path: '/orders/sales?page=0&pageSize=50', scope: 'orders.read', description: 'Paged sales orders' },
    { label: 'Purchase Orders', method: 'GET', path: '/orders/purchases?page=0&pageSize=50', scope: 'orders.read', description: 'Paged purchase orders' },
    { label: 'Fleet Vehicles', method: 'GET', path: '/fleet/vehicles?page=0&pageSize=50', scope: 'fleet.read', description: 'Vehicle availability surface' },
    { label: 'Fleet Drivers', method: 'GET', path: '/fleet/drivers?page=0&pageSize=50', scope: 'fleet.read', description: 'Driver availability surface' },
    { label: 'Audit Summary', method: 'GET', path: '/audit/summary', scope: 'audit.read', description: 'Audit summary and cases' },
    { label: 'Reporting Datasets', method: 'GET', path: '/reporting/datasets', scope: 'reporting.read', description: 'Reporting dataset catalog' },
    { label: 'Business Event Definitions', method: 'GET', path: '/business-events/definitions', scope: 'events.read', description: 'Webhook event catalog' }
  ];

  readonly endpointOptions = this.endpoints.map(endpoint => ({
    label: `${endpoint.method} ${endpoint.label}`,
    value: endpoint.path
  }));

  activeTab = signal<PlatformTab>('dashboard');
  isLoading = signal(false);
  playgroundLoading = signal(false);
  summary = signal<ApiPlatformSummary | null>(null);
  clients = signal<ApiClientView[]>([]);
  scopes = signal<ApiScopeDefinition[]>([]);
  ratePlans = signal<ApiRateLimitPolicy[]>([]);
  usageLogs = signal<ApiUsageLogView[]>([]);
  webhooks = signal<WebhookSubscriptionView[]>([]);
  oneTimeSecret = signal<{ title: string; secret: string } | null>(null);
  playgroundResult = signal<PlaygroundResult | null>(null);

  canManageCredentials = false;
  canUsePlayground = false;
  canViewUsage = false;
  canManageWebhooks = false;

  clientDialogVisible = false;
  webhookDialogVisible = false;
  selectedPlaygroundClientId: string | null = null;
  selectedEndpointPath = this.endpoints[0].path;

  clientForm: Partial<ApiClientView> & ApiClientCreateRequest = this.emptyClientForm();
  webhookForm: Partial<WebhookSubscriptionView> = this.emptyWebhookForm();
  playground = {
    environment: 'SANDBOX',
    clientId: '',
    clientSecret: '',
    method: 'GET' as PlaygroundMethod,
    path: '/status',
    body: ''
  };

  metrics = computed<PageMetric[]>(() => {
    const summary = this.summary();
    const logs = this.usageLogs();
    const failedCalls = logs.filter(log => (log.statusCode || 0) >= 400).length;
    return [
      { label: 'Credentials', value: summary?.clientCount ?? this.clients().length, tone: 'info' },
      { label: 'Usage Events', value: summary?.usageCount ?? logs.length, tone: 'default' },
      { label: 'Webhooks', value: summary?.webhookCount ?? this.webhooks().length, tone: 'success' },
      { label: 'Recent Errors', value: failedCalls, tone: failedCalls ? 'danger' : 'success' },
      { label: 'Sandbox Limit', value: '60/min', hint: '5k/day', tone: 'warn' }
    ];
  });

  get scopeOptions(): { label: string; value: string }[] {
    return this.scopes().map(scope => ({
      label: `${scope.label} (${scope.code})`,
      value: scope.code
    }));
  }

  ngOnInit(): void {
    this.canManageCredentials = this.permissionService.hasPermission(app_permissions.developer_platform.credentials.manage);
    this.canUsePlayground = this.permissionService.hasPermission(app_permissions.developer_platform.playground.use);
    this.canViewUsage = this.permissionService.hasPermission(app_permissions.developer_platform.usage.view);
    this.canManageWebhooks = this.permissionService.hasPermission(app_permissions.developer_platform.webhooks.manage);
    this.route.queryParamMap.subscribe(params => {
      this.activeTab.set(this.resolveTab(params.get('tab')));
    });
    this.load();
  }

  setActiveTab(tab: PlatformTab): void {
    const resolvedTab = this.resolveTab(tab);
    this.activeTab.set(resolvedTab);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: resolvedTab },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  visibleTabs(): { value: PlatformTab; label: string; icon: string }[] {
    return this.tabs.filter(tab => {
      if (tab.value === 'usage') return this.canViewUsage;
      if (tab.value === 'playground') return this.canUsePlayground;
      if (tab.value === 'webhooks') return this.canManageWebhooks;
      return true;
    });
  }

  load(): void {
    this.isLoading.set(true);
    forkJoin({
      summary: this.developerPlatformService.summary(),
      scopes: this.developerPlatformService.scopes(),
      ratePlans: this.developerPlatformService.ratePlans(),
      clients: this.developerPlatformService.clients(),
      usage: this.developerPlatformService.usage(0, 50),
      webhooks: this.developerPlatformService.webhooks()
    }).subscribe({
      next: result => {
        this.summary.set(result.summary.data ?? null);
        this.scopes.set(result.scopes.data ?? []);
        this.ratePlans.set(result.ratePlans.data ?? []);
        this.clients.set(result.clients.data ?? []);
        this.usageLogs.set(result.usage.items ?? []);
        this.webhooks.set(result.webhooks.data ?? []);
        this.isLoading.set(false);
      },
      error: error => {
        this.isLoading.set(false);
        this.messageService.add({ severity: 'error', summary: 'Developer Platform', detail: error?.error?.message ?? 'Unable to load developer platform data.' });
      }
    });
  }

  openClientDialog(client?: ApiClientView): void {
    this.clientForm = client
      ? { ...client, scopes: [...(client.scopes || [])] }
      : this.emptyClientForm();
    this.clientDialogVisible = true;
  }

  onClientEnvironmentChange(): void {
    if (this.clientForm.environment === 'SANDBOX') {
      this.clientForm.ratePlan = 'SANDBOX';
    } else if (this.clientForm.ratePlan === 'SANDBOX') {
      this.clientForm.ratePlan = 'STANDARD';
    }
  }

  clientRatePlanOptions(): { label: string; value: string }[] {
    const environment = this.clientForm.environment || 'SANDBOX';
    return this.ratePlans()
      .filter(plan => plan.environment === environment)
      .map(plan => ({ label: `${plan.label} - ${plan.requestsPerMinute}/min, ${plan.requestsPerDay}/day`, value: plan.planCode }));
  }

  saveClient(): void {
    if (!this.clientForm.clientName?.trim()) {
      this.messageService.add({ severity: 'warn', summary: 'Credential Name Required' });
      return;
    }
    if (!this.clientForm.scopes?.length) {
      this.messageService.add({ severity: 'warn', summary: 'Scopes Required' });
      return;
    }

    const request: ApiClientCreateRequest = {
      clientName: this.clientForm.clientName.trim(),
      description: this.clientForm.description?.trim(),
      environment: this.clientForm.environment || 'SANDBOX',
      ratePlan: this.clientForm.ratePlan || (this.clientForm.environment === 'SANDBOX' ? 'SANDBOX' : 'STANDARD'),
      allowedOrigins: this.clientForm.allowedOrigins?.trim(),
      scopes: this.clientForm.scopes || []
    };

    if (this.clientForm.apiClientId) {
      this.developerPlatformService.updateClient({ ...this.clientForm, ...request } as ApiClientView).subscribe({
        next: () => {
          this.clientDialogVisible = false;
          this.messageService.add({ severity: 'success', summary: 'Credential Saved' });
          this.load();
        },
        error: (error: any) => this.messageService.add({ severity: 'error', summary: 'Save Failed', detail: error?.error?.message ?? 'Unable to save credential.' })
      });
      return;
    }

    this.developerPlatformService.createClient(request).subscribe({
      next: response => {
        this.clientDialogVisible = false;
        this.showClientSecret(response.data);
        this.messageService.add({ severity: 'success', summary: 'Credential Saved' });
        this.load();
      },
      error: (error: any) => this.messageService.add({ severity: 'error', summary: 'Save Failed', detail: error?.error?.message ?? 'Unable to save credential.' })
    });
  }

  rotateClientSecret(client: ApiClientView): void {
    this.confirmationService.confirm({
      header: 'Rotate Secret',
      message: `Rotate the secret for ${client.clientName}? The old active secret is revoked after rotation.`,
      icon: 'pi pi-key',
      accept: () => {
        this.developerPlatformService.rotateSecret(client).subscribe({
          next: response => {
            this.showClientSecret(response.data);
            this.messageService.add({ severity: 'success', summary: 'Secret Rotated' });
            this.load();
          },
          error: error => this.messageService.add({ severity: 'error', summary: 'Rotation Failed', detail: error?.error?.message ?? 'Unable to rotate secret.' })
        });
      }
    });
  }

  revokeClient(client: ApiClientView): void {
    this.confirmationService.confirm({
      header: 'Revoke Credential',
      message: `Revoke ${client.clientName}? Existing tokens and future token requests will be denied.`,
      icon: 'pi pi-ban',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.developerPlatformService.revokeClient(client).subscribe({
          next: () => {
            this.messageService.add({ severity: 'success', summary: 'Credential Revoked' });
            this.load();
          },
          error: error => this.messageService.add({ severity: 'error', summary: 'Revoke Failed', detail: error?.error?.message ?? 'Unable to revoke credential.' })
        });
      }
    });
  }

  openWebhookDialog(webhook?: WebhookSubscriptionView): void {
    this.webhookForm = webhook ? { ...webhook } : this.emptyWebhookForm();
    this.webhookDialogVisible = true;
  }

  saveWebhook(): void {
    if (!this.webhookForm.name?.trim() || !this.webhookForm.eventCode?.trim() || !this.webhookForm.targetUrl?.trim()) {
      this.messageService.add({ severity: 'warn', summary: 'Webhook Details Required' });
      return;
    }
    if (this.webhookForm.webhookSubscriptionId) {
      this.developerPlatformService.updateWebhook(this.webhookForm as WebhookSubscriptionView).subscribe({
        next: () => {
          this.webhookDialogVisible = false;
          this.messageService.add({ severity: 'success', summary: 'Webhook Saved' });
          this.load();
        },
        error: (error: any) => this.messageService.add({ severity: 'error', summary: 'Save Failed', detail: error?.error?.message ?? 'Unable to save webhook.' })
      });
      return;
    }

    this.developerPlatformService.createWebhook(this.webhookForm).subscribe({
      next: response => {
        this.webhookDialogVisible = false;
        this.oneTimeSecret.set({ title: 'Webhook signing secret', secret: response.data.signingSecret });
        this.messageService.add({ severity: 'success', summary: 'Webhook Saved' });
        this.load();
      },
      error: (error: any) => this.messageService.add({ severity: 'error', summary: 'Save Failed', detail: error?.error?.message ?? 'Unable to save webhook.' })
    });
  }

  rotateWebhookSecret(webhook: WebhookSubscriptionView): void {
    this.confirmationService.confirm({
      header: 'Rotate Signing Secret',
      message: `Rotate the signing secret for ${webhook.name}?`,
      icon: 'pi pi-key',
      accept: () => {
        this.developerPlatformService.rotateWebhookSecret(webhook).subscribe({
          next: response => {
            this.oneTimeSecret.set({ title: 'Webhook signing secret', secret: response.data.signingSecret });
            this.messageService.add({ severity: 'success', summary: 'Webhook Secret Rotated' });
            this.load();
          },
          error: error => this.messageService.add({ severity: 'error', summary: 'Rotation Failed', detail: error?.error?.message ?? 'Unable to rotate webhook secret.' })
        });
      }
    });
  }

  deleteWebhook(webhook: WebhookSubscriptionView): void {
    this.confirmationService.confirm({
      header: 'Delete Webhook',
      message: `Delete ${webhook.name}?`,
      icon: 'pi pi-trash',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.developerPlatformService.deleteWebhook(webhook).subscribe({
          next: () => {
            this.messageService.add({ severity: 'success', summary: 'Webhook Deleted' });
            this.load();
          },
          error: error => this.messageService.add({ severity: 'error', summary: 'Delete Failed', detail: error?.error?.message ?? 'Unable to delete webhook.' })
        });
      }
    });
  }

  playgroundCredentialOptions(): { label: string; value: string }[] {
    return this.clients()
      .filter(client => client.status === 'ACTIVE' && client.environment === this.playground.environment)
      .map(client => ({ label: `${client.clientName} (${client.clientId})`, value: client.clientId }));
  }

  selectPlaygroundClient(): void {
    const client = this.clients().find(item => item.clientId === this.selectedPlaygroundClientId);
    if (client) {
      this.playground.clientId = client.clientId;
      this.playground.environment = client.environment;
    }
  }

  selectEndpoint(): void {
    const endpoint = this.currentEndpoint();
    if (!endpoint) return;
    this.playground.method = endpoint.method;
    this.playground.path = endpoint.path;
  }

  executePlayground(): void {
    if (this.playground.environment === 'PRODUCTION') {
      this.confirmationService.confirm({
        header: 'Run Production Request',
        message: 'This will execute against production credentials and the production external API environment.',
        icon: 'pi pi-exclamation-triangle',
        accept: () => this.executePlaygroundNow()
      });
      return;
    }
    this.executePlaygroundNow();
  }

  async executePlaygroundNow(): Promise<void> {
    if (!this.playground.clientId || !this.playground.clientSecret) {
      this.messageService.add({ severity: 'warn', summary: 'Credentials Required', detail: 'Enter a client ID and client secret.' });
      return;
    }
    this.playgroundLoading.set(true);
    this.playgroundResult.set(null);
    const started = performance.now();
    try {
      const tokenBody = new URLSearchParams();
      tokenBody.set('grant_type', 'client_credentials');
      tokenBody.set('client_id', this.playground.clientId);
      tokenBody.set('client_secret', this.playground.clientSecret);
      tokenBody.set('scope', this.currentEndpoint()?.scope || '');
      tokenBody.set('environment', this.playground.environment);

      const tokenResponse = await fetch(this.tokenUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
        body: tokenBody
      });
      const tokenJson = await this.safeJson(tokenResponse);
      const accessToken = (tokenJson as any)?.access_token || (tokenJson as any)?.accessToken;
      if (!tokenResponse.ok || !accessToken) {
        this.playgroundResult.set({
          status: tokenResponse.status,
          headers: this.headersToRecord(tokenResponse.headers),
          body: tokenJson,
          elapsedMs: Math.round(performance.now() - started)
        });
        return;
      }

      const requestHeaders: Record<string, string> = {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/json',
        'X-Request-Id': `portal-${Date.now()}`
      };
      const hasBody = this.playground.method !== 'GET' && !!this.playground.body?.trim();
      if (hasBody) {
        requestHeaders['Content-Type'] = 'application/json';
        requestHeaders['Idempotency-Key'] = `portal-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      }

      const apiResponse = await fetch(`${this.externalBaseUrl}${this.normalizedPath()}`, {
        method: this.playground.method,
        headers: requestHeaders,
        body: hasBody ? this.playground.body : undefined
      });

      this.playgroundResult.set({
        status: apiResponse.status,
        headers: this.headersToRecord(apiResponse.headers),
        body: await this.safeJson(apiResponse),
        elapsedMs: Math.round(performance.now() - started)
      });
    } catch (error) {
      this.playgroundResult.set({
        status: 0,
        headers: {},
        body: { error: error instanceof Error ? error.message : 'Request failed' },
        elapsedMs: Math.round(performance.now() - started)
      });
    } finally {
      this.playgroundLoading.set(false);
    }
  }

  generatedCurl(): string {
    const secret = this.playground.clientSecret || '<client_secret>';
    const clientId = this.playground.clientId || '<client_id>';
    const scope = this.currentEndpoint()?.scope || '<scope>';
    const tokenCurl = [
      `curl -X POST "${this.tokenUrl}"`,
      `  -H "Content-Type: application/x-www-form-urlencoded"`,
      `  -d "grant_type=client_credentials"`,
      `  -d "client_id=${clientId}"`,
      `  -d "client_secret=${secret}"`,
      `  -d "scope=${scope}"`,
      `  -d "environment=${this.playground.environment}"`
    ].join(' \\\n');

    const requestLines = [
      `curl -X ${this.playground.method} "${this.externalBaseUrl}${this.normalizedPath()}"`,
      `  -H "Authorization: Bearer <access_token>"`,
      `  -H "Accept: application/json"`
    ];
    if (this.playground.method !== 'GET') {
      requestLines.push(`  -H "Content-Type: application/json"`);
      requestLines.push(`  -H "Idempotency-Key: <unique-operation-key>"`);
      requestLines.push(`  -d '${this.playground.body || '{}'}'`);
    }
    return `${tokenCurl}\n\n${requestLines.join(' \\\n')}`;
  }

  async copyText(text: string, summary: string): Promise<void> {
    await navigator.clipboard?.writeText(text);
    this.messageService.add({ severity: 'success', summary });
  }

  planLabel(planCode: string, environmentName: string): string {
    return this.ratePlans().find(plan => plan.planCode === planCode && plan.environment === environmentName)?.label ?? planCode;
  }

  environmentSeverity(environmentName: string): 'success' | 'warn' | 'info' | 'secondary' {
    if (environmentName === 'PRODUCTION') return 'warn';
    if (environmentName === 'SANDBOX') return 'info';
    return 'secondary';
  }

  statusSeverity(status: string): 'success' | 'warn' | 'danger' | 'secondary' {
    if (status === 'ACTIVE') return 'success';
    if (status === 'SUSPENDED') return 'warn';
    if (status === 'REVOKED') return 'danger';
    return 'secondary';
  }

  responseSeverity(status: number): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (!status) return 'secondary';
    if (status >= 200 && status < 300) return 'success';
    if (status === 429) return 'warn';
    if (status >= 400) return 'danger';
    return 'info';
  }

  private currentEndpoint(): PlaygroundEndpoint | undefined {
    return this.endpoints.find(endpoint => endpoint.path === this.selectedEndpointPath);
  }

  private normalizedPath(): string {
    const value = this.playground.path?.trim() || '/status';
    return value.startsWith('/') ? value : `/${value}`;
  }

  private async safeJson(response: Response): Promise<unknown> {
    const text = await response.text();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }

  private headersToRecord(headers: Headers): Record<string, string> {
    const record: Record<string, string> = {};
    headers.forEach((value, key) => {
      record[key.toLowerCase()] = value;
    });
    return record;
  }

  private showClientSecret(data: ApiClientCreateResponse | ApiClientSecretRotationResponse): void {
    this.oneTimeSecret.set({ title: `${data.client.clientName} client secret`, secret: data.clientSecret });
    this.playground.clientId = data.client.clientId;
    this.playground.clientSecret = data.clientSecret;
    this.playground.environment = data.client.environment;
    this.selectedPlaygroundClientId = data.client.clientId;
  }

  private emptyClientForm(): Partial<ApiClientView> & ApiClientCreateRequest {
    return {
      clientName: '',
      description: '',
      environment: 'SANDBOX',
      ratePlan: 'SANDBOX',
      allowedOrigins: '',
      scopes: []
    };
  }

  private emptyWebhookForm(): Partial<WebhookSubscriptionView> {
    return {
      name: '',
      eventCode: 'SHIPMENT_DELIVERY_CONFIRMED',
      targetUrl: '',
      active: true
    };
  }

  private resolveTab(tab: string | null): PlatformTab {
    return this.tabs.find(item => item.value === tab)?.value || 'dashboard';
  }
}
